import assert from 'assert';
import _ from 'lodash';
import moment from 'moment';
import React from 'react';
import Helmet from 'react-helmet';
import { Description, DescriptionText } from '../components/Description.jsx';
import { FormInformationDialog } from '../components/FormInformationDialog';
import { Modal } from '../components/Modal.jsx';
import { ConferencePageHeader } from '../components/PageHeader.jsx';
import Tippy from '../components/ReactTippy.jsx';
import WorkScrollGroup from '../components/ScrollGroup.jsx';
import { WorkHourModal } from '../components/WorkHourModal.jsx';
import { WorkUnapprovedSearchForm } from '../components/WorkUnapprovedSearchForm.jsx';
import WorksInfinityScrollLoad from '../components/WorksInfinityScrollLoad.jsx';
import UnapprovedWorksPageHeader from '../components/UnapprovedWorksPageHeader';
import { QuestionIcon } from '../components/icons/QuestionIcon.jsx';
import WorkApproval from '../components/modals/WorkApproval.jsx';
import Confirmation from '../components/modals/common/Confirmation.jsx';
import Information from '../components/modals/common/Information.jsx';
import { FieldService } from '../services/Fields.js';
import UserProfileService from '../services/UserProfiles.js';
import { WorkApprovalService } from '../services/WorkApprovals.js';
import { WorkScheduleService } from '../services/Works.js';
import LocalStorageService from '../services/utilities/LocalStorageService.js';
import ReloadHelper from '../services/utilities/ReloadHelper.js';
import SessionStorageService from '../services/utilities/SessionStorageService.js';
import EventManager, { EventType } from '../utilities/EventManager.js';
import { LayoutDelegate } from '../utilities/LayoutDelegate.js';
import { isApiFlagOff, onceAsyncRequest } from '../utilities/commonUtil.js';
import {
  API_FLAG,
  APPROVAL_FAIL_TYPE,
  CALL_FROM_PAGE,
  CONFERENCE_MODE,
  DATE_TIME_FORMAT,
  HEADER_CONSTANTS,
  LIST_LIMIT,
  MAX_WORKS_APPROVED,
  PARTNER_APPROVAL_NAME,
  PRIVATE_COMPANY_FLAG,
  PRIVATE_COMPANY_SEARCH_STATE,
  SCHEDULE_STATUS,
  UNAPPROVED_TYPE,
  USER_ROLES_IDS,
  WORK_EVENT,
  WORK_HOUR_ZONES,
  WORK_MODAL_LIST,
  WORK_TYPE,
  WORK_UNAPPROVED_PAGE_TITLE,
} from '../utilities/constants.js';
import dialogLoader from '../utilities/dialogLoader';
import { messageUtility } from '../utilities/messageUtility.js';
import { match } from '../utilities/parts.js';
import {
  isPrimaryCompanyRoleId,
  isTargetRoleId,
} from '../utilities/roleUtility.js';
import urlParamManager from '../utilities/urlParamManager.js';

/**
 * @description 作業スケジュール画面や作業実績一覧が承認・確認されていない
 */
@LayoutDelegate()
export default class WorksUnapproved extends React.Component {
  constructor(props) {
    super(props);

    assert(['1', '2'].includes(this.props.vw));
    this.isPrimaryCompanyRoleId = isPrimaryCompanyRoleId();
    this.state = {
      scheduleFlag: this.props.isWorkSchedule
        ? SCHEDULE_STATUS.schedule.id
        : SCHEDULE_STATUS.result.id,
      selected: false,
      showModal: false,
      selectedModalId: undefined,
      selectedWorkId: undefined, // ダイアログを開くときに指定している作業のID
      selectedWorkGroups: [], // ワークグループの選択状態を保持する配列
      selectedWorkIdList: [], // チェックボックスで選択状態になる作業のIDリスト
      isSelectedWorkLocked: false, // 選択した作業が自動ロックされているかどうか
      workGroups: [], // 表示しているワークグループの配列
      workGroupTotal: 0,
      totalWorkItems: 0,
      totalWorkBlankManHours: 0,
      searchParams: {}, // 検索条件
      tempSearchParams: {}, // 検索条件(保存用)
      isOpenAllGroups: true,
      reload: false, // 再読み込みフラグ
      searchPrivatePartnerCompanyState: PRIVATE_COMPANY_SEARCH_STATE.NotSet, //別途業者検索フラグ (NotSet: 未指定, NotPrivate: 別途業者以外, Private: 別途業者のみ)
      workApprovalButtonEnabled: false,
      isLoading: true, // ロード中フラグ
      usePartnerCompanyApprovalFlag:
        SessionStorageService.getUsePartnerCompanyApprovalFlag() ===
        API_FLAG.On, // 協力会社承認機能ご利用フラグ
      partnerApprovalName: this.props.isWorkSchedule
        ? PARTNER_APPROVAL_NAME.schedule
        : PARTNER_APPROVAL_NAME.result, // 協力会社承認機能_承認名: 受領 or 確定
      partnerApprovalWorkId: null, // 協力会社承認_対象作業ID(単数用)
      modalMessage: '', // モーダル用メッセージ
      workColumnsSettings: {
        list_order: [],
        conference_order: [],
      },
      fieldId: SessionStorageService.getFieldId(),
      workResultNotReflectedFlag: false,
      useCheckInReflectToResultFlag: false,
      approvalTypeText: '',
      pageTitle: this.getPageTitle(),
      approvalErrorTitle: '',
      approvalErrorMessage: '',
    };

    this.pageName =
      this.props.isWorkSchedule === true
        ? SCHEDULE_STATUS.schedule.name
        : SCHEDULE_STATUS.result.name; // ページ名: 予定 or 実績

    // 読込処理が連続で実行された場合に並列で実行されることを防ぐ
    // 前のAPI完了後の処理と今のAPI完了後の処理が重複して不整合な表示となることを防ぐ
    this.parallelLoadGuardObj = null;

    EventManager.registerEvent(WORK_EVENT.LOAD_GROUP_DATA);
    EventManager.addEventListener(WORK_EVENT.LOAD_GROUP_DATA, async isBack => {
      const response = await FieldService.info(this.state.fieldId);
      if (response.result) {
        const field = _.get(response, 'data.entities.field');
        const usePartnerCompanyApprovalFlag = _.get(
          field,
          'use_partner_company_approval_flag'
        );
        if (isApiFlagOff(usePartnerCompanyApprovalFlag)) {
          this.setState({
            isLoading: false,
            workGroups: [],
            workGroupTotal: 0,
            totalWorkItems: 0,
          });
          this.showDialogPartnerCompanyApprovalIsOff();
          return;
        }
      }
      if (!isBack) {
        this.setState({ reload: true });
      }
    });
    EventManager.addEventListener(EventType.RESET_FILTER_UNAPPROVED_WORK_PAGE, this.resetSearchParam);

    // メモ化用
    this.workGroupsPartitionParamMemo = null;
    this.workGroupsPartitionResultMemo = null;

    this.approvedListIds = [];
    this.unapprovedListIds = [];
    this.partnerApprovalListsIds = [];
    this.partnerUnapprovalListsIds = [];
    this.workSuspendedListsIds = [];
    this.workIds = [];
    this.isPartnerCompanyRoleId = isTargetRoleId(USER_ROLES_IDS.PARTNER_COMPANY);
  }

  async componentDidMount() {
    const { fieldId, scheduleFlag } = this.state;
    EventManager.trigger(EventType.ACCESS_UNAPPROVED_WORK_PAGE);
    const fieldInfoResponse = await FieldService.info(fieldId, {});
    if (fieldInfoResponse.result) {
      const field = _.get(fieldInfoResponse, 'data.entities.field');
      const usePartnerCompanyApprovalFlag = _.get(
        field,
        'use_partner_company_approval_flag'
      );
      if (isApiFlagOff(usePartnerCompanyApprovalFlag)) {
        this.setState({ isLoading: false });
        this.showDialogPartnerCompanyApprovalIsOff();
        return;
      }
      this.setState({
        useCheckInReflectToResultFlag:
          _.get(field, 'use_check_in_reflect_to_result_flag') &&
          moment().isAfter(
            _.get(field, 'use_check_in_reflect_to_result_start_date'),
            'day'
          ),
      });
    }
    const workColumnsSettingsResponse = await FieldService.workColumnsSettings(
      fieldId,
      {
        schedule_flag: scheduleFlag,
      }
    );
    const work_columns_settings = _.get(
      workColumnsSettingsResponse,
      'data.entities.work_columns_settings'
    );
    const list_order = this.getColumnOrderList(
      work_columns_settings.list_order
    );
    const conference_order = this.getColumnOrderList(
      work_columns_settings.conference_order
    );
    const defaultSearchParams = await this.getDefaultSearchParamsValue();
    const workApprovalButtonEnabled = await this.isVisibleWorkApprovalControl(
      fieldId
    );
    this.setState({
      workApprovalButtonEnabled,
      searchParams: _.cloneDeep(defaultSearchParams),
      tempSearchParams: _.cloneDeep(defaultSearchParams),
      workColumnsSettings: {
        list_order,
        conference_order,
      },
    });
  }

  componentDidUpdate(prevProps, prevState) {
    const { searchParams, reload } = this.state;
    if (prevState.searchParams !== searchParams) {
      const searchPrivatePartnerCompanyState = this.detectPivatePartnerCompanyFlagInSearchParams();
      this.resetResult();
      this.setState(
        {
          searchPrivatePartnerCompanyState,
          workGroups: [],
          workGroupTotal: 0,
          isLoading: true,
        },
        () => {
          this.loadGroupData(() => {});
        }
      );
    } else if (!_.isEqual(prevState.reload, reload) && reload) {
      this.resetResult();
      this.reLoadGroupData();
      EventManager.trigger(EventType.REFRESH_UNAPPROVED_WORKS_COUNT);
    }
  }

  showDialogPartnerCompanyApprovalIsOff = () => {
    dialogLoader.show(FormInformationDialog, {
      title: messageUtility.getMessage('E-001-450'),
      message: messageUtility.getMessage('E-001-451'),
      onClose: () => {
        SessionStorageService.setUsePartnerCompanyApprovalFlag(API_FLAG.Off);
        dialogLoader.close();
        window.location.replace('/conference/0/0/0/Dashboard:1/Dashboard:0');
      },
    });
  }

  componentWillUnmount() {
    EventManager.trigger(EventType.ACCESS_UNAPPROVED_WORK_PAGE);
    EventManager.removeEventListener(EventType.RESET_FILTER_UNAPPROVED_WORK_PAGE, this.resetSearchParam);
  }

  getColumnOrderList = listWork => {
    return _.sortBy(
      _.filter(listWork, item => item.display),
      item => item.order_number
    );
  };

  resetListsValues = () => {
    this.approvedListIds = [];
    this.unapprovedListIds = [];
    this.partnerApprovalListsIds = [];
    this.partnerUnapprovalListsIds = [];
    this.workSuspendedListsIds = [];
    this.workIds = [];
  };

  updateApproveUnapprovedList = () => {
    this.resetListsValues();
    _.forEach(this.state.workGroups, workGroup => {
      _.forEach(workGroup.works, work => {
        this.workIds.push(work.id);
        const workSuspended = work.state_handling_flag === API_FLAG.On;
        if (!workSuspended) {
          if (work.approved_num > 0 || work.auto_lock_flg === API_FLAG.On) {
            this.approvedListIds.push(work.id);
          } else {
            this.unapprovedListIds.push(work.id);
          }
          if (work.partner_company_approval_flag === API_FLAG.Off) {
            this.partnerUnapprovalListsIds.push(work.id);
          } else {
            this.partnerApprovalListsIds.push(work.id);
          }
        } else {
          this.workSuspendedListsIds.push(work.id);
        }
      });
    });
  };
  /**
   * 検索パラメータにprivate_partner_company_flagが存在するかどうかによってsearchPrivatePartnerCompanyStateを差し替える
   *  */
  detectPivatePartnerCompanyFlagInSearchParams = () => {
    const { searchParams } = this.state;
    if (_.has(searchParams, 'private_partner_company_flag')) {
      return _.toNumber(searchParams.private_partner_company_flag) ===
        PRIVATE_COMPANY_FLAG.Private
        ? PRIVATE_COMPANY_SEARCH_STATE.Private
        : PRIVATE_COMPANY_SEARCH_STATE.NotPrivate;
    }
    return PRIVATE_COMPANY_SEARCH_STATE.NotSet;
  };

  concatWorkGroupLoadMore = (currentWorkGroups, loadedGroups) => {
    const reflectedTotalShowKeys = ['field_tree_id', 'work_date'];
    let preReflectedWorkGroup = _.findLast(currentWorkGroups, {
      work_hour_zone: WORK_HOUR_ZONES.day.id,
    });
    _.forEach(loadedGroups, group => {
      if (group.work_hour_zone === WORK_HOUR_ZONES.day.id) {
        const conditionsForNotShow = _.pick(
          preReflectedWorkGroup,
          reflectedTotalShowKeys
        );
        group.isShowReflectedWorkTotal = !_.isEqual(
          _.pick(group, reflectedTotalShowKeys),
          conditionsForNotShow
        );
        preReflectedWorkGroup = group;
      }
    });

    return _.concat(currentWorkGroups, loadedGroups);
  };

  // 作業グループ情報の取得
  loadGroupData = async notify => {
    const { workGroups, reload } = this.state;

    if (reload) {
      // リロード中は実行しない
      this.setState({ isLoading: false });
      return;
    }
    this.parallelLoadGuardObj = {};
    const selfGuardObj = this.parallelLoadGuardObj;

    this.setState({ isLoading: true });

    const offset = _.size(workGroups);
    const {
      items: loadedGroups,
      totalCount: groupTotal,
      totalWorksCount,
      workResultNotReflectedFlag,
    } = await this.loadWorkGroups(offset);

    await this.loadWorkInWorkGroups(loadedGroups, offset);

    // 連続で実行された場合に最後に呼ばれた処理のみ反映する
    if (selfGuardObj !== this.parallelLoadGuardObj) {
      return;
    }

    this.setState(
      {
        workGroups: this.concatWorkGroupLoadMore(workGroups, loadedGroups),
        workGroupTotal: groupTotal,
        totalWorkItems: totalWorksCount,
        isLoading: false,
        workResultNotReflectedFlag,
      },
      () => {
        this.updateApproveUnapprovedList();
        notify();
      }
    );
  };

  addLoadGroupData = async notify => {
    // 追加読込の場合はisLoadingを見て連続で呼ばれないようにする
    if (this.state.isLoading) {
      return;
    }
    this.setState({ isLoading: true });
    await this.loadGroupData(notify);
  };

  // 作業グループ情報の再取得
  reLoadGroupData = async callback => {
    const { workGroups, workGroupTotal } = this.state;
    const loadedMaxCount = _.size(workGroups);
    const {
      allItems,
      totalCount,
      totalWorksCount,
      workResultNotReflectedFlag,
    } = await ReloadHelper.takeLoad(
      this.loadWorkGroupsAndWorksFirstPage,
      loadedMaxCount,
      loadedMaxCount === workGroupTotal
    );

    // 「さらに表示する」で追加された分を追加
    const workGroupsAdded = await this.reloadWorkInWorkGroups(allItems);

    this.setState(
      {
        workGroups: this.concatWorkGroupLoadMore([], workGroupsAdded),
        workGroupTotal: totalCount,
        totalWorkItems: totalWorksCount,
        workResultNotReflectedFlag,
        reload: false,
      },
      () => {
        this.updateApproveUnapprovedList();
        callback && callback();
      }
    );
  };

  /**
   * 作業グループ複数の情報の取得
   */
  loadWorkGroups = async offset => {
    const { searchParams, fieldId } = this.state;
    const params = {
      ...searchParams,
      offset,
    };
    let value = {
      items: [],
      totalCount: 0,
      totalWorksCount: 0,
    };
    const { result, data } = await WorkScheduleService.mainSchedules(
      fieldId,
      params
    );
    if (result) {
      value = {
        items: data.entities.work_groups,
        totalCount: data.total,
        totalWorksCount: data.total_work,
        workResultNotReflectedFlag: data.reflected_work_flag,
      };
    }
    return value;
  };

  // WorkGroupsとWorksの最初のページ(10件)を取得
  loadWorkGroupsAndWorksFirstPage = async offset => {
    const {
      items: loadedGroups,
      totalCount: groupTotal,
      totalWorksCount,
      workResultNotReflectedFlag,
    } = await this.loadWorkGroups(offset);

    await this.loadWorkInWorkGroups(loadedGroups);

    return {
      items: loadedGroups,
      totalCount: groupTotal,
      totalWorksCount,
      workResultNotReflectedFlag,
    };
  };

  worksConvert = (items, itemParent) => {
    // BLDE_V2-16558 旧APIのデータ形式にあわせてデータを作る
    // そうしないとフロント側のどこで情報を読み取っているか不明なため
    const itemParentConvert = _.pick(itemParent, [
      'company_name',
      'company_name_1',
      'field_id',
      'field_tree_id',
      'foreman_user_id',
      'foreman_user_name_mei',
      'foreman_user_name_sei',
      'job_id',
      'job_name',
      'should_approved_num',
      'work_date',
    ]);
    return _.map(items, item => ({ ...item, ...itemParentConvert }));
  };

  /**
   * 初期表示時
   * ワークグループ複数の内部のワーク情報を取得する
   */
  loadWorkInWorkGroups = async (workGroups, offset = 0) => {
    const { searchParams, fieldId } = this.state;
    if (!_.isArray(workGroups) || _.isEmpty(workGroups)) {
      this.setState({ totalWorkBlankManHours: 0 });
      return;
    }

    // 初期起動時には ワークグループIDを複数指定して同時にデータを取得する
    let params = _.cloneDeep(searchParams);
    // ワークグループ複数のIDを配列にする
    params.work_group_ids = _.map(workGroups, workGroup => workGroup.id);
    params.limit = LIST_LIMIT.WORK.CHILD;
    params = _.omit(params, ['date_from', 'date_to', 'work_hour_zones']);
    const { data, result } = await WorkScheduleService.schedules(
      fieldId,
      params
    );
    if (!result) {
      return;
    }

    // 取得したデータをワークグループでループして内部の値を更新する
    _.forEach(workGroups, workGroup => {
      const responseWorkGroup = _.find(data.entities.works, [
        'work_group_id',
        workGroup.id,
      ]);
      if (_.isUndefined(responseWorkGroup)) {
        return;
      }

      const itemParent = responseWorkGroup;
      const items = this.worksConvert(itemParent.work_list, itemParent);
      workGroup.works = items;
      workGroup.worksLoadComp = _.size(items) === itemParent.works_total;
      workGroup.totalSumHours = itemParent.total_sum_hours;
    });

    const totalWorkBlankManHours = _.get(data, 'total_work_blank_man_hours');
    this.setState(prevState => ({
      totalWorkBlankManHours: offset
        ? prevState.totalWorkBlankManHours + totalWorkBlankManHours
        : totalWorkBlankManHours,
    }));
  };

  /**
   * ワークグループ単独の情報を取得する
   */
  loadWorksUnit = async ({
    fieldId,
    workGroupId,
    searchParams,
    offset,
    limit = LIST_LIMIT.WORK.CHILD,
  }) => {
    if (match(workGroupId, [null, undefined, ''])) {
      return {
        items: [],
        totalCount: 0,
        totalSumHours: '0.0',
      };
    }
    let params = {};
    params.work_group_ids = [workGroupId];
    if (!_.isUndefined(searchParams)) {
      params = { ...params, ...searchParams };
    }
    if (!_.isUndefined(offset)) {
      params.offset = offset;
    }
    params.limit = limit;

    const workData = {
      items: [],
      totalCount: 0,
      totalSumHours: '0.0',
    };
    const { data, result } = await WorkScheduleService.schedules(
      fieldId,
      params
    );
    if (result) {
      const itemParent = _.get(data, 'entities.works[0]');
      const { work_list, works_total, total_sum_hours } = itemParent;
      workData.items = this.worksConvert(work_list, itemParent);
      workData.totalCount = works_total;
      workData.totalSumHours = total_sum_hours;
    }

    return workData;
  };

  /**
   * ワークグループ単独の内容を前回値に従って読み込む
   * 既存が30件のデータの場合、新しく読み込む場合も10件x3で読み込む
   */
  reloadWorks = async (prevWorkGroup, nextWorkGroup) => {
    // 既に読込済みの場合は何もしない
    if (
      // グループ内で1件目のWorkを作成した場合はprevWorkGroupはundefined
      !prevWorkGroup ||
      // 完了済みの場合は追加なし
      nextWorkGroup.worksLoadComp ||
      // 10件以上存在するが「さらに表示する」を1回も押していない場合は同じになる
      // 総件数が同じではなくて表示した件数が10件=10件の確認
      (!prevWorkGroup.worksLoadComp &&
        _.size(prevWorkGroup.works) === _.size(nextWorkGroup.works))
    ) {
      return nextWorkGroup;
    }

    const loadedMaxCount = _.size(prevWorkGroup.works);
    const { allItems, totalCount, totalSumHours } = await ReloadHelper.takeLoad(
      (offset, limit) =>
        this.loadWorksUnit({
          fieldId: this.state.fieldId,
          workGroupId: prevWorkGroup.id,
          searchParams: this.state.searchParams,
          offset,
          limit,
        }),
      loadedMaxCount,
      prevWorkGroup.worksLoadComp,
      _.size(nextWorkGroup.works), //既に読込済みなので開始位置を指定
      LIST_LIMIT.WORK.CHILD
    );

    //読込済みのworksと追加で取得したworksを結合
    const nextWorks = nextWorkGroup.works.concat(allItems);
    const result = {
      ...nextWorkGroup,
      works: nextWorks,
      worksLoadComp: _.size(nextWorks) === totalCount,
    };
    if (!_.isUndefined(totalSumHours)) {
      result.totalSumHours = totalSumHours;
    }
    return result;
  };

  reloadWorkInWorkGroups = async workGroups => {
    return await Promise.all(
      _.map(workGroups, async nextWorkGroup => {
        const prevWorkGroup = _.find(this.state.workGroups, [
          'id',
          nextWorkGroup.id,
        ]);
        return await this.reloadWorks(prevWorkGroup, nextWorkGroup);
      })
    );
  };

  // 検索結果のリセット
  resetResult = () => {
    this.setState({
      selectedWorkIdList: [],
      selectedWorkGroups: [],
    });
  };

  /**
   * @description オープンするモーダルIDを設定
   * @param {string} modalId モーダルID(WORK_MODAL_LIST)
   * @param {string} message メッセージ
   */
  handleOpenModalById = (modalId, message = '') => {
    this.setState({ selectedModalId: modalId, showModal: true, message });
  };

  // 画面上に存在する作業を全て選択/選択解除
  handleSelectAll = isSelected => {
    const { workGroups } = this.state;

    // グループチェックボックス
    let selectedWorkGroups = [];
    let selectedWorkIdList = [];
    if (isSelected) {
      selectedWorkGroups = _.map(workGroups, workGroup => workGroup.id);
      // ワークグループ内項目
      _.forEach(workGroups, workGroup => {
        _.forEach(workGroup.works, work => {
          if (!_.isNull(work.man_hours)) {
            selectedWorkIdList.push(work.id);
          }
        });
      });
    }

    this.setState(
      {
        selectedWorkGroups,
        selectedWorkIdList,
      },
      () => this.updateApproveUnapprovedList()
    );
  };

  // 作業を選択(1件)
  handleWorkSelect = workId => {
    const selectedWorkIdList = _.cloneDeep(this.state.selectedWorkIdList);
    const workIdIndex = _.indexOf(selectedWorkIdList, workId);
    const select = workIdIndex === -1;
    if (select) {
      // 選択リストに入っていなければ選択
      selectedWorkIdList.push(workId);
    } else {
      // 選択リストに入っていれば選択解除
      selectedWorkIdList.splice(workIdIndex, 1);
    }

    this.updateWorkGroupSelect(workId, select, selectedWorkIdList);

    this.setState({ selectedWorkIdList }, () => {
      this.updateApproveUnapprovedList();
    });
  };

  /**
   * ワーク選択時にワークグループの選択状態を切り替える処理
   */
  updateWorkGroupSelect = (workId, select, selectedWorkIdList) => {
    const selectedWorkGroups = _.cloneDeep(this.state.selectedWorkGroups);

    // 項目選択時にグループ項目の選択を切り替える
    // workId が所属しているworkGroupを求める
    const workGroup = _.find(this.state.workGroups, workGroup =>
      _.some(workGroup.works, { id: workId })
    );
    // そのworkGroupが、選択されているかどうかをIndexで判定する
    const workGroupIndex = _.indexOf(selectedWorkGroups, workGroup.id);
    if (select) {
      if (
        _.every(workGroup.works, work =>
          _.includes(selectedWorkIdList, work.id)
        ) &&
        workGroupIndex === -1
      ) {
        selectedWorkGroups.push(workGroup.id);
      }
    } else {
      if (workGroupIndex !== -1) {
        selectedWorkGroups.splice(workGroupIndex, 1);
      }
    }
    this.setState({ selectedWorkGroups });
  };

  // グループを選択
  handleGroupSelect = (groupId, privatePartnerCompanyFlag, isSelected) => {
    const { selectedWorkIdList, selectedWorkGroups, workGroups } = this.state;

    // group
    const targetGroup = _.find(workGroups, ['id', groupId]);
    if (!targetGroup) {
      return;
    }

    let newSelectedWorkGroups;
    if (isSelected) {
      if (!_.includes(selectedWorkGroups, targetGroup.id)) {
        newSelectedWorkGroups = _.concat(selectedWorkGroups, targetGroup.id);
      }
      _.forEach(workGroups, workGroup => {
        if (workGroup.id === groupId) {
          _.forEach(workGroup.works, work => {
            if (
              !_.includes(selectedWorkIdList, work.id) &&
              !_.isNull(work.man_hours)
            ) {
              selectedWorkIdList.push(work.id);
            }
          });
        }
      });
    } else {
      if (_.includes(selectedWorkGroups, targetGroup.id)) {
        newSelectedWorkGroups = _.pullAll(selectedWorkGroups, [targetGroup.id]);
      }
      _.forEach(workGroups, workGroup => {
        if (workGroup.id === groupId) {
          _.forEach(workGroup.works, work => {
            const index = _.indexOf(selectedWorkIdList, work.id);
            if (index !== -1) {
              selectedWorkIdList.splice(index, 1);
            }
          });
        }
      });
    }

    this.setState({
      selectedWorkIdList,
      selectedWorkGroups: newSelectedWorkGroups,
    });
  };

  /**
   * スクロールグループの「さらに表示」でデータが追加された時のイベント
   */
  handleUpdateWorksInGroup = ({ workGroupId, works, worksLoadComp }) => {
    const stateWorkGroups = _.cloneDeep(this.state.workGroups);
    const targetGroup = _.find(stateWorkGroups, ['id', workGroupId]);

    targetGroup.works = works;
    targetGroup.worksLoadComp = worksLoadComp;

    // ワークグループの選択状態を解除する
    const selectedWorkGroups = _.cloneDeep(this.state.selectedWorkGroups);
    const workGroupIndex = _.indexOf(selectedWorkGroups, workGroupId);

    if (workGroupIndex !== -1) {
      selectedWorkGroups.splice(workGroupIndex, 1);
    }

    this.setState({
      workGroups: stateWorkGroups,
      selectedWorkGroups,
    });
  };

  handleToggleOpenAllWorkGroups = () => {
    this.setState(prevState => ({
      isOpenAllGroups: !prevState.isOpenAllGroups,
    }));
  };

  /**
   * @description モーダルIDが設定されていた場合、該当モーダルを画面に表示
   * @param {string} modalId モーダルID(WORK_MODAL_LIST)
   */
  setModalById = modalId => {
    const {
      selectedWorkId,
      scheduleFlag,
      fieldId,
      workApprovalButtonEnabled,
      isSelectedWorkLocked,
    } = this.state;
    switch (modalId) {
      case WORK_MODAL_LIST.ManHourInput:
        return (
          <WorkHourModal
            onUpdate={this.handleReload}
            onClose={this.handleCloseModal}
            workId={selectedWorkId}
            scheduleFlag={scheduleFlag}
            unapprovedFlag={true}
          />
        );
      case WORK_MODAL_LIST.ApprovalStatus:
        return (
          <WorkApproval
            onClose={this.handleCloseModal}
            onCancelWorkApproval={this.handleWorkApprovalCancel}
            workId={selectedWorkId}
            fieldId={fieldId}
            workApprovalButtonEnabled={workApprovalButtonEnabled}
            autoLock={isSelectedWorkLocked}
            onApproved={this.handleReload}
            onShowDialogDeniedApproval={
              this.handleVisibleApprovalWorksErrorModal
            }
          />
        );
      default:
        return null;
    }
  };

  /**
   * @description 選択作業を承認
   * @param {string} fieldId 現場ID
   * @param {array} selectedWorkIdList 選択作業リスト
   */
  handleWorkApproval = onceAsyncRequest(async (fieldId, selectedWorkIdList) => {
    await UserProfileService.show(LocalStorageService.getUserId(), {}).then(
      async response => {
        if (!response.result) {
          return;
        }
        const userProfile = response.data.entities.user_profile;
        const firstName = userProfile.name_mei; // ユーザの名前
        const lastName = userProfile.name_sei; // ユーザの苗字
        await WorkApprovalService.workApproval(
          fieldId,
          selectedWorkIdList,
          firstName,
          lastName,
          true
        ).then(response => {
          const errorCode = _.get(response, 'data.data.entities.error_code');
          if (errorCode) {
            this.handleCloseModal();
            this.handleReload();
            return this.handleVisibleApprovalWorksErrorModal(
              messageUtility.getMessage('E-001-439'),
              messageUtility.getMessage(errorCode)
            );
          }
          if (!response.result) {
            return;
          }
          if (_.has(response, 'message') && response.message === '') {
            this.handleCloseModal();
          } else if (!_.isUndefined(response.message)) {
            // 承認/承認解除結果ダイヤログのタイトル
            WORK_MODAL_LIST.DynamicSuccessTitle = `作業${this.pageName}の承認${
              _.includes(response.message, '完了') ? '完了' : ''
            }`;
            WORK_MODAL_LIST.DynamicSuccessMessage = response.message;
            this.handleOpenModalById(WORK_MODAL_LIST.DynamicSuccess);
          }
          this.handleReload();
        });
      }
    );
  });

  /**
   * 「選択項目を承認」「選択項目の承認解除」ボタンの表示非表示
   */
  isVisibleWorkApprovalControl = async fieldId => {
    let result = true;

    await WorkApprovalService.list(fieldId, '0').then(response => {
      if (!response.result) {
        return;
      }
      const workApprovals = response.data.entities.work_approvals;

      result = _.some(
        workApprovals,
        workApproval => workApproval.is_operatable
      );
    });

    return result;
  };

  handleWorkApprovalCancel = () => {
    if (this.state.isSelectedWorkLocked) {
      return WorkApprovalService.cancelWorkApproval(
        this.state.fieldId,
        [this.state.selectedWorkId]
      ).then(response => {
        if (response.result === false) {
          return;
        }
        this.setState({ isSelectedWorkLocked: false });
        this.handleReload();
        this.handleOpenModalById(WORK_MODAL_LIST.ApprovalStatus);
      });
    }
  };

  /**
   * @description 協力会社承認/否認 実行準備(単数用)
   * @param {number} workId 作業ID
   * @param {boolean} isApproval true: 承認, false: 否認
   */
  partnerApprovalSinglePreparation = (workId, isApproval) => {
    if (!isApproval) {
      return;
    }
    const { PartnerApprovalConfirm } = WORK_MODAL_LIST;

    this.handleOpenModalById(PartnerApprovalConfirm);
    this.setState({ partnerApprovalWorkId: workId });
  };

  handleResponsePartnerApprovalRun = (response, isBulkApproval) => {
    const { isWorkSchedule } = this.props;
    const errorCode = _.get(response, 'data.data.entities.error_code');
    if (errorCode) {
      this.resetPartnerApprovalValue();
      return this.handleVisibleApprovalWorksErrorModal(
        messageUtility.getMessage(
          `${isBulkApproval ? 'E-001-441' : 'E-001-439'}`
        ),
        messageUtility.getMessage(errorCode)
      );
    }
    if (response.result) {
      WORK_MODAL_LIST.DynamicSuccessTitle = isWorkSchedule
        ? '作業予定の受領完了'
        : '作業実績の確定完了';
      WORK_MODAL_LIST.DynamicSuccessMessage = isWorkSchedule
        ? '選択した作業予定の受領が完了しました。'
        : '選択した作業実績の確定が完了しました。';
      this.handleOpenModalById(WORK_MODAL_LIST.DynamicSuccess);
    } else {
      const modalMessage = 'エラーが発生しました。';
      this.handleOpenModalById(WORK_MODAL_LIST.DynamicDesignationMessage);
      this.setState({ modalMessage });
    }
  };

  /**
   * @description 協力会社承認/否認 実行
   */
  partnerApprovalRun = () => {
    const { selectedWorkIdList, partnerApprovalWorkId, fieldId } = this.state;
    const isBulkApproval = _.isNull(partnerApprovalWorkId);
    const workIds = isBulkApproval
      ? selectedWorkIdList
      : [partnerApprovalWorkId];

    if (_.isEmpty(workIds)) {
      let modalMessage = `作業${this.pageName}が選択されていません。`;
      this.handleOpenModalById(WORK_MODAL_LIST.DynamicDesignationMessage);
      return this.setState({ modalMessage });
    }

    WorkApprovalService.partnerApproval(fieldId, workIds, isBulkApproval).then(
      response => {
        this.handleResponsePartnerApprovalRun(response, isBulkApproval);
        this.handleReload();
      }
    );
  };

  renderScrollGroup = groups => {
    const {
      scheduleFlag,
      searchParams,
      isOpenAllGroups,
      selectedWorkGroups,
      selectedWorkIdList,
      usePartnerCompanyApprovalFlag,
      partnerApprovalName,
      workColumnsSettings,
    } = this.state;
    const { parentId, selected } = this.props;
    const isDisplayReflectTotalColumn = this.isDisplayReflectTotalColumn();

    return _.map(groups, (group, key) => (
      <WorkScrollGroup
        workGroupData={group}
        onUpdateWorksInGroup={this.handleUpdateWorksInGroup}
        loadWorksUnit={this.loadWorksUnit}
        key={`scroll-group-${key}-${group.id || key}`}
        id={`scroll-group-${key}-${group.id || key}`}
        scheduleFlag={scheduleFlag}
        unapprovedFlag={true}
        parent={parentId}
        selected={selected}
        selectedList={selectedWorkIdList}
        group_selected={_.includes(selectedWorkGroups, group.id)}
        privatePartnerCompanyFlag={group.private_partner_company_flag}
        searchParams={searchParams}
        groupId={group.id}
        isOpened={isOpenAllGroups}
        onWorkSelect={workId => {
          this.handleWorkSelect(workId);
        }}
        onGroupSelect={(groupId, privatePartnerCompanyFlag, isSelected) => {
          this.handleGroupSelect(
            groupId,
            privatePartnerCompanyFlag,
            isSelected
          );
        }}
        onApprovalStatusClick={this.handleApprovalStatusClick}
        onManHourEditClick={(e, id) => this.onManHourEditClick(e, id)}
        usePartnerCompanyApprovalFlag={usePartnerCompanyApprovalFlag}
        partnerApprovalName={partnerApprovalName}
        onPartnerApprovalSinglePreparation={
          this.partnerApprovalSinglePreparation
        }
        workColumnsSettings={workColumnsSettings}
        isDisplayReflectTotalColumn={isDisplayReflectTotalColumn}
      />
    ));
  };

  // 作業予定の表示
  setScrollGroup = workGroups => {
    return this.renderScrollGroup(workGroups);
  };

  handleReload = () => {
    this.setState({
      reload: true,
    });
  };

  /**
   * @description モーダル用メッセージリセット
   */
  resetModalMessage = () => {
    if (this.state.usePartnerCompanyApprovalFlag) {
      this.resetPartnerApprovalValue();
    } else {
      this.handleCloseModal();
    }
    this.setState({ modalMessage: '' });
  };

  /**
   * @description 協力会社承認_値リセット
   */
  resetPartnerApprovalValue = () => {
    this.handleCloseModal();
    this.setState({ partnerApprovalWorkId: null });
  };

  /**
   * @description モーダルをクローズ
   */
  handleCloseModal = () => {
    this.setState({
      showModal: false,
      selectedModalId: WORK_MODAL_LIST.Unloaded,
    });
  };

  // 行の作業承認状態のクリックイベントのハンドリング
  handleApprovalStatusClick = (id, isLocked = false) => () => {
    this.setState({ selectedWorkId: id, isSelectedWorkLocked: isLocked });
    this.handleOpenModalById(WORK_MODAL_LIST.ApprovalStatus);
  };

  /**
   * 行の人工編集のクリックイベントのハンドリング
   * @param {number}id
   */
  onManHourEditClick = id => {
    this.setState({ selectedWorkId: id });
    this.handleOpenModalById(WORK_MODAL_LIST.ManHourInput);
  };

  // 検索条件の変更ハンドリング
  setSearchParam = () => {
    this.setState({ searchParams: _.cloneDeep(this.state.tempSearchParams) });
  };

  // 検索条件のリセット
  resetSearchParam = () => {
    this.getDefaultSearchParamsValue().then(searchParams => {
      this.setState({
        searchParams,
        selectedWorkGroups: [],
        tempSearchParams: _.cloneDeep(searchParams),
      });
    });
  };

  // 検索条件(保存用)の変更ハンドリング
  setTempSearchParam = (name, value) => {
    this.setState(prevState => {
      return {
        tempSearchParams: {
          ...prevState.tempSearchParams,
          [name]: value,
        },
      };
    });
  };

  getDefaultSearchParamsValue = async () => {
    const dateTo = moment()
      .add(this.props.isWorkSchedule ? 1 : 0, 'days')
      .format(DATE_TIME_FORMAT.slashDateFormat); // workResult: toDate is today, workSchedule: toDate is tomorrow
    const dateFrom = moment(dateTo)
      .subtract(30, 'day')
      .format(DATE_TIME_FORMAT.slashDateFormat);
    const unapprovedType = this.isPrimaryCompanyRoleId
      ? SessionStorageService.getDefaultUnapprovedFilterValue() ||
        UNAPPROVED_TYPE.UNAPPROVED.value
      : UNAPPROVED_TYPE.ALL.value;
    return {
      call_from: CALL_FROM_PAGE.UNAPPROVED_WORK_PAGE,
      schedule_flag: this.props.isWorkSchedule
        ? SCHEDULE_STATUS.schedule.id
        : SCHEDULE_STATUS.result.id,
      date_from: dateFrom,
      date_to: dateTo,
      work_hour_zones: [
        WORK_HOUR_ZONES.day.id,
        WORK_HOUR_ZONES.night.id,
        WORK_HOUR_ZONES.midnight.id,
      ],
      unapproved_type: unapprovedType,
    };
  };

  renderReflectedWorkTooltip = () => {
    return (
      <div
        style={{
          maxWidth: 260,
          wordWrap: 'break-word',
          color: '#FFF',
          fontSize: 14,
          lineHeight: '1.5',
          textAlign: 'left',
        }}
      >
        {messageUtility.getMessage('I-001-549')}
      </div>
    );
  };

  isDisplayReflectTotalColumn = () => {
    return !this.props.isWorkSchedule && this.state.useCheckInReflectToResultFlag;
  };

  tableHeader = () => {
    const isConferenceMode =
      SessionStorageService.getItem('is_conference') === CONFERENCE_MODE.on.key;
    const {
      workColumnsSettings,
      isOpenAllGroups,
      usePartnerCompanyApprovalFlag,
      partnerApprovalName,
    } = this.state;
    const workColumnItems = isConferenceMode
      ? workColumnsSettings.conference_order
      : workColumnsSettings.list_order;
    const isDisplayReflectTotalColumn = this.isDisplayReflectTotalColumn();

    return (
      <thead className='data-table__header'>
        <tr className=''>
          <th className='data-table__head data-table__head--collapse'>
            開閉
            <button
              className='btn-icon--xs--dark'
              onClick={this.handleToggleOpenAllWorkGroups}
            >
              <i
                className={`i-warrow-${
                  isOpenAllGroups ? 'top' : 'bottom'
                } i-2x`}
              />
            </button>
          </th>
          {isConferenceMode ? null : (
            <React.Fragment>
              {/* チェックボックス */}
              <th className='data-table__head' />
            </React.Fragment>
          )}
          {_.map(workColumnItems, item => {
            if (item.type === 'approvals' && usePartnerCompanyApprovalFlag) {
              return (
                <React.Fragment key={item.type}>
                  <th className='data-table__head'>元請会社承認</th>
                  <th className='data-table__head'>
                    協力会社{partnerApprovalName}
                  </th>
                </React.Fragment>
              );
            } else {
              return (
                <React.Fragment key={item.type}>
                  <th className='data-table__head'>{item.name}</th>
                  {item.type === 'man_hours' && isDisplayReflectTotalColumn && (
                    <th className='data-table__head'>
                      <div className='data-table__head--guide'>
                        入場人数
                        <Tippy
                          duration={10}
                          distance={24}
                          arrow={true}
                          arrowType='sharp'
                          delay={[100, 100]}
                          placement='bottom'
                          followCursor={true}
                          html={this.renderReflectedWorkTooltip()}
                        >
                          <QuestionIcon className='icon--2x question-icon' />
                        </Tippy>
                      </div>
                    </th>
                  )}
                </React.Fragment>
              );
            }
          })}
        </tr>
      </thead>
    );
  };

  // レンダリング時に別途協力会社を振り分ける
  memoizeWorkGroupsPartition = workGroups => {
    // 重い処理ではないがレンダリングの度に呼ばれるのでメモ化しておく
    if (this.workGroupsPartitionParamMemo === workGroups) {
      return this.workGroupsPartitionResultMemo;
    }

    // 別途協力会社の作業の振り分け
    const [notPrivateWorkGroups, privateWorkGroups] = _.partition(workGroups, {
      private_partner_company_flag: PRIVATE_COMPANY_FLAG.NotPrivate,
    });

    const result = {
      notPrivateWorkGroups: notPrivateWorkGroups || [],
      privateWorkGroups: privateWorkGroups || [],
    };

    this.workGroupsPartitionParamMemo = workGroups;
    this.workGroupsPartitionResultMemo = result;

    return result;
  };

  getPageTitle = () => {
    let pageTitle = this.isPrimaryCompanyRoleId
      ? WORK_UNAPPROVED_PAGE_TITLE.UNAPPROVED_RESULT
      : WORK_UNAPPROVED_PAGE_TITLE.UNRECEIVED_RESULT;
    if (this.props.isWorkSchedule) {
      pageTitle = this.isPrimaryCompanyRoleId
        ? WORK_UNAPPROVED_PAGE_TITLE.UNAPPROVED_SCHEDULE
        : WORK_UNAPPROVED_PAGE_TITLE.UNRECEIVED_SCHEDULE;
    }
    return pageTitle;
  };

  handleApprovalWork = () => {
    if (_.size(this.state.selectedWorkIdList) > MAX_WORKS_APPROVED) {
      this.setState({
        approvalTypeText: APPROVAL_FAIL_TYPE.PRIME_CONTRACTOR,
      });
      this.handleOpenModalById(WORK_MODAL_LIST.ReachMaxNumber);
    } else {
      this.handleOpenModalById(WORK_MODAL_LIST.ConfirmSelected);
    }
  };

  handlePartnerApprovalConfirm = () => {
    const { selectedWorkIdList, scheduleFlag } = this.state;
    if (_.size(selectedWorkIdList) > MAX_WORKS_APPROVED) {
      if (scheduleFlag === SCHEDULE_STATUS.schedule.id) {
        this.setState({
          approvalTypeText: APPROVAL_FAIL_TYPE.PARTNER_APPROVAL_SCHEDULE,
        });
      } else {
        this.setState({
          approvalTypeText: APPROVAL_FAIL_TYPE.PARTNER_APPROVAL_RESULT,
        });
      }
      this.handleOpenModalById(WORK_MODAL_LIST.ReachMaxNumber);
    } else {
      this.handleOpenModalById(WORK_MODAL_LIST.PartnerApprovalConfirm);
    }
  };

  handleVisibleApprovalWorksErrorModal = (
    approvalErrorTitle = '',
    approvalErrorMessage = ''
  ) =>
    this.setState({
      approvalErrorTitle,
      approvalErrorMessage,
    });

  render() {
    const isConferenceMode =
      SessionStorageService.getItem('is_conference') === CONFERENCE_MODE.on.key;
    const isWorkSchedule = this.props.isWorkSchedule;
    const {
      totalWorkBlankManHours,
      pageTitle,
      workGroups,
      searchPrivatePartnerCompanyState,
      selectedWorkIdList,
      selectedWorkGroups,
      workApprovalButtonEnabled,
      usePartnerCompanyApprovalFlag,
      partnerApprovalName,
      scheduleFlag,
      tempSearchParams,
      workGroupTotal,
      totalWorkItems,
      fieldId,
      isLoading,
      showModal,
      selectedModalId,
      modalMessage,
      approvalTypeText,
      approvalErrorTitle,
      approvalErrorMessage,
    } = this.state;
    const {
      notPrivateWorkGroups,
      privateWorkGroups,
    } = this.memoizeWorkGroupsPartition(workGroups);
    const isPrivateCompanyWorkVisible =
      searchPrivatePartnerCompanyState !==
        PRIVATE_COMPANY_SEARCH_STATE.NotPrivate &&
      !_.isEmpty(privateWorkGroups);
    if (isPrivateCompanyWorkVisible) {
      SessionStorageService.setPrivateBatchWorkListIds(
        _.flatMap(privateWorkGroups, workGroups =>
          _.map(workGroups.works, 'id')
        )
      );
    } else {
      SessionStorageService.setPrivateBatchWorkListIds([]);
    }
    const loadedWorkTotal = _.size(workGroups);

    const slide = urlParamManager.getSlideParam();
    const buttonDisabled = _.isEmpty(selectedWorkIdList);
    const isDisabledUncheckAllButton =
      _.isEmpty(selectedWorkIdList) && _.isEmpty(selectedWorkGroups);

    const btnPrimeCompanyApproval = workApprovalButtonEnabled &&
      this.isPrimaryCompanyRoleId && (
        <li className='btn-menu__item'>
          <button
            id='modal-open-confirm-selected'
            className={`btn--effective ${buttonDisabled ? 'btn-disabled' : ''}`}
            disabled={buttonDisabled}
            onClick={this.handleApprovalWork}
          >
            選択項目を承認
          </button>
        </li>
      );
    const btnPartnerCompanyApprovalConfirm = this.isPartnerCompanyRoleId && (
      <li className='btn-menu__item'>
        <button
          id='modal-open-confirm-selected'
          className={`btn--effective ${buttonDisabled ? 'btn-disabled' : ''}`}
          disabled={buttonDisabled}
          onClick={this.handlePartnerApprovalConfirm}
        >
          {`選択項目を${partnerApprovalName}`}
        </button>
      </li>
    );

    return (
      <div id='main' className='main'>
        {slide[0] === '0' && ( // リロード時他ページのtitleを上書きする問題の対策
          <Helmet>
            <title>
              {HEADER_CONSTANTS.conference} - {pageTitle}
            </title>
          </Helmet>
        )}

        <ConferencePageHeader
          title={pageTitle}
          middleComponent={
            usePartnerCompanyApprovalFlag && <UnapprovedWorksPageHeader />
          }
        />
        <div id='scrolll-parent' className='main__body'>
          <div className='schedulelist'>
            {!isConferenceMode &&
              (btnPrimeCompanyApproval || btnPartnerCompanyApprovalConfirm) && (
                <div className='schedulelist__header'>
                  <ul className='btn-menu'>
                    {btnPrimeCompanyApproval}
                    {btnPartnerCompanyApprovalConfirm}
                  </ul>
                </div>
              )}

            <WorkUnapprovedSearchForm
              onSetSearchParam={this.setSearchParam}
              onResetSearchParam={this.resetSearchParam}
              scheduleFlag={scheduleFlag}
              workGroupTotal={workGroupTotal}
              totalWorkItems={totalWorkItems}
              fieldId={fieldId}
              isLoading={isLoading}
              partnerApprovalName={partnerApprovalName}
              tempSearchParams={tempSearchParams}
              onSetTempSearchParam={this.setTempSearchParam}
            />

            {!!totalWorkBlankManHours && (
              <div className='schedulelist__list-header'>
                <Description type='primary'>
                  <DescriptionText>
                    <div className='schedulelist__caution'>
                      <i className='icon-warning_round' />
                      未入力の人工が
                      <span className='schedulelist__caution__count'>
                        {totalWorkBlankManHours}件
                      </span>
                      あります
                    </div>
                  </DescriptionText>
                </Description>
              </div>
            )}

            <div className='schedulelist__list-header'>
              <ul className='btn-menu btn-menu__unapproved'>
                {!isConferenceMode && (
                  <React.Fragment>
                    <li className='btn-menu__item'>
                      <button
                        id='btn-check-all'
                        className={
                          !_.isEqual(
                            _.size(selectedWorkGroups),
                            _.size(workGroups)
                          )
                            ? `btn--stay`
                            : `btn--sub btn-disabled`
                        }
                        onClick={() => this.handleSelectAll(true)}
                      >
                        表示項目をすべて選択
                      </button>
                    </li>
                    <li className='btn-menu__item'>
                      <span className='selected-work__text'>
                        {_.size(selectedWorkIdList)}件選択中
                      </span>
                    </li>
                    <li className='btn-menu__item'>
                      <button
                        id='btn-uncheck-all'
                        className={
                          isDisabledUncheckAllButton
                            ? `btn-text--sub disabled`
                            : `btn-text`
                        }
                        disabled={isDisabledUncheckAllButton}
                        onClick={() => this.handleSelectAll(false)}
                      >
                        選択中の項目を解除
                      </button>
                    </li>
                  </React.Fragment>
                )}
                <li className='btn-menu__item'>
                  <span>※</span>
                  <button
                    disabled
                    className='status-ticket--autolook conf-status-ticket status-ticket--caution--large'
                  >
                    承認済(0/0)
                  </button>
                  <span className='auto-lock-explain'>
                    {messageUtility.getMessage('I-001-548')}
                  </span>
                </li>
              </ul>
            </div>

            <div className='schedulelist__content'>
              <div
                className='schedulelist__list'
                id='scroll-parent-non-private-work'
              >
                <table className='data-table'>
                  {this.tableHeader()}
                  {this.setScrollGroup(notPrivateWorkGroups)}
                  {isPrivateCompanyWorkVisible && (
                    <React.Fragment>
                      <tbody>
                        <tr>
                          <td
                            style={{
                              background: '#1A1A1A',
                              color: 'white',
                              padding: '6px',
                              fontSize: 12,
                            }}
                            colSpan='50'
                          >
                            以下、別途協力会社
                          </td>
                        </tr>
                      </tbody>
                      {this.setScrollGroup(privateWorkGroups)}
                    </React.Fragment>
                  )}
                </table>

                <WorksInfinityScrollLoad
                  targetId={'scroll-parent-non-private-work'}
                  callback={this.addLoadGroupData}
                  loaded={workGroupTotal <= loadedWorkTotal}
                  total={workGroupTotal}
                  isLoading={isLoading}
                />
                {isLoading || workGroupTotal ? null : (
                  <div className='table-has-no-data'>
                    <div className='table-has-no-data__message'>
                      {messageUtility.getMessage('I-000-005')}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        <Modal modalRoot='root' modalState={showModal}>
          {this.setModalById(selectedModalId)}
        </Modal>
        <Information
          title={WORK_MODAL_LIST.DynamicSuccessTitle}
          visible={selectedModalId === WORK_MODAL_LIST.DynamicSuccess}
          message={WORK_MODAL_LIST.DynamicSuccessMessage}
          buttonText={messageUtility.getMessage('I-000-017')}
          onClose={this.resetModalMessage}
          multilineText={true}
        />
        <Confirmation
          visible={selectedModalId === WORK_MODAL_LIST.ConfirmSelected}
          title={messageUtility.getMessage('I-001-407', `作業${this.pageName}`)}
          message={messageUtility.getMessage(
            'I-001-404',
            `作業${this.pageName}`
          )}
          confirmButtonText={messageUtility.getMessage('I-000-007')}
          cancelButtonText={messageUtility.getMessage('I-000-008')}
          onConfirm={() => this.handleWorkApproval(fieldId, selectedWorkIdList)}
          onCancel={this.handleCloseModal}
        />

        {/* 協力会社承認_確認ダイアログ */}
        <Confirmation
          visible={selectedModalId === WORK_MODAL_LIST.PartnerApprovalConfirm}
          title={`
          作業${this.pageName}の${partnerApprovalName}`}
          message={messageUtility.getMessage(
            'I-001-415',
            partnerApprovalName,
            this.pageName
          )}
          confirmButtonText={partnerApprovalName}
          cancelButtonText='戻る'
          onConfirm={this.partnerApprovalRun}
          onCancel={this.resetPartnerApprovalValue}
        />

        {/* メッセージ指定_動的 */}
        <Information
          visible={
            selectedModalId === WORK_MODAL_LIST.DynamicDesignationMessage
          }
          message={modalMessage}
          buttonText='了解'
          onClose={this.resetModalMessage}
        />
        <Information
          title={`一括${approvalTypeText}に失敗しました`}
          visible={selectedModalId === WORK_MODAL_LIST.ReachMaxNumber}
          message={messageUtility.getMessage(
            'I-001-547',
            approvalTypeText,
            isWorkSchedule
              ? WORK_TYPE.WORK_SCHEDULE.NAME
              : WORK_TYPE.WORK_RESULT.NAME,
            MAX_WORKS_APPROVED
          )}
          buttonText={messageUtility.getMessage('I-000-017')}
          onClose={this.resetModalMessage}
          multilineText={true}
        />
        <Information
          title={approvalErrorTitle}
          visible={!!approvalErrorMessage}
          message={approvalErrorMessage}
          buttonText={messageUtility.getMessage('I-000-017')}
          onClose={this.handleVisibleApprovalWorksErrorModal}
          multilineText={true}
        />
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/pages/WorksUnapproved.jsx